import { useMemo, useState } from 'react';
import { useLoaderData } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InputText } from 'primereact/inputtext';
import { Divider } from 'primereact/divider';
import IconText from '../../components/common/IconText/IconText';
import Card from '../../components/common/Card/Card';
import ContentSection from '../../components/common/ContentSection/ContentSection';
import StatChip from '../../components/common/StatChip/StatChip';
import MemberCard from '../../components/common/MemberCard/MemberCard';
import VoteBar from '../../components/common/VoteBar/VoteBar';
import { getColors } from '../../Constants';
import { capitalizeFirstLetter } from '../../utils/texts';
import type { PartiesData, PartyStats, PartyMember } from '../../api';
import { useAppContext } from '../../context/AppContext';
import { useFormatters } from '../../utils/texts';
import { useVoteColors } from '../../utils/hooks';
import { useLocalizationLabels } from '../../context/LocalizationLabelContext';
import './Parties.css';

type PartyWithMeta = {
    partyData: PartiesData;
    stats: PartyStats;
    isInCoalition: boolean;
    isInactive: boolean;
};

type IndexedMember = {
    member: PartyMember;
    partyZkratka: string;
    partyBarva: string;
};

// Diacritic-insensitive match so e.g. "bartusek" finds "Bartůšek".
// Combining Diacritical Marks block (U+0300-U+036F), built via charCode to avoid escape-sequence ambiguity.
const COMBINING_DIACRITICS_REGEX = new RegExp('[' + String.fromCharCode(0x0300) + '-' + String.fromCharCode(0x036f) + ']', 'g');

const normalizeForSearch = (value: string) =>
    value
        .normalize('NFD')
        .replace(COMBINING_DIACRITICS_REGEX, '')
        .toLowerCase();

function Parties() {
    const { t, i18n } = useTranslation('parties');
    const { basicData } = useAppContext();

    const response = useLoaderData() as [PartiesData[], PartyStats[], unknown, unknown];
    const partiesData = response[0];
    const partiesStats = response[1];
    const currentLocale = i18n.language;
    const formatter = useFormatters(currentLocale);
    const voteColors: { [key: string]: string } = useVoteColors();
    const { voteOptionLabels } = useLocalizationLabels();

    const [query, setQuery] = useState('');
    const trimmedQuery = query.trim();

    // Flat, deduplicated member list for search: the loader already fetches every
    // member (clenove) per party, so no extra endpoint is needed. When a member
    // appears under more than one party (moved during the term), the active
    // record wins so search surfaces their current party.
    const memberIndex = useMemo(() => {
        const byId = new Map<number, IndexedMember>();
        partiesData.forEach((partyData) => {
            partyData.clenove.forEach((member) => {
                const existing = byId.get(member.id);
                if (!existing || member.aktivni === 1) {
                    byId.set(member.id, { member, partyZkratka: partyData.strana_zkratka, partyBarva: partyData.strana_barva });
                }
            });
        });
        return Array.from(byId.values());
    }, [partiesData]);

    const searchResults = useMemo(() => {
        if (!trimmedQuery) return [];
        const normalizedQuery = normalizeForSearch(trimmedQuery);
        return memberIndex
            .filter(({ member, partyZkratka }) =>
                normalizeForSearch(`${member.jmeno} ${member.stat ?? ''} ${partyZkratka}`).includes(normalizedQuery)
            )
            .sort((a, b) => normalizeForSearch(a.member.jmeno).localeCompare(normalizeForSearch(b.member.jmeno)));
    }, [memberIndex, trimmedQuery]);

    const cardComponent = ({ partyData, stats, isInactive }: PartyWithMeta) => {
        const mandateTranslation = t('mandate', { ns: 'translation', count: partyData.pocet_aktivnich });
        const commonColorProps = { iconAndHighlightColor: isInactive ? getColors().darkGray : partyData.strana_barva };
        const percent = formatter.percent2From100(stats.celkova_ucast);

        const summaryVotesEntries = stats.celkove_hlasy ?? [];
        const getVoteLabel = (item: { id: number; code: string }) =>
            capitalizeFirstLetter(voteOptionLabels[item.id]?.label_universal ?? item.code);

        return (
            <Card key={partyData.strana_id} className='card-list-item' link={`${partyData.strana_id}`}>
                <div className='link-container'>
                    <h4 style={{ color: partyData.strana_barva }}>{partyData.strana_zkratka}</h4>
                    {isInactive && (
                        <IconText
                            icon='unpublished'
                            text={capitalizeFirstLetter(t('inactive', { ns: 'translation' }))}
                            highlightSubString={capitalizeFirstLetter(t('inactive', { ns: 'translation' }))}
                            {...commonColorProps}
                        />
                    )}
                </div>
                <div className='card-list-item-content'>
                    <div className='card-stats-row'>
                        <IconText
                            icon='chair'
                            text={`${partyData.pocet_aktivnich} ${mandateTranslation}`}
                            highlightSubString={`${partyData.pocet_aktivnich}`}
                            {...commonColorProps}
                        />
                        <IconText
                            icon='group'
                            text={`${percent} ${t('attendance', { ns: 'meetings' })}`}
                            highlightSubString={`${percent}`}
                            {...commonColorProps}
                        />
                    </div>
                    <VoteBar
                        items={summaryVotesEntries}
                        colors={voteColors}
                        getLabel={getVoteLabel}
                        formatPercent={formatter.percentFrom100}
                    />
                </div>
            </Card>
        );
    };

    const weightedAttendance = (group: PartyWithMeta[]): number | null => {
        let weightSum = 0;
        let weightTotal = 0;

        group.forEach(({ partyData, stats }) => {
            if (partyData.pocet_aktivnich > 0) {
                weightSum += stats.celkova_ucast * partyData.pocet_aktivnich;
                weightTotal += partyData.pocet_aktivnich;
            }
        });

        return weightTotal > 0 ? weightSum / weightTotal : null;
    };

    const buildGroupSummary = (group: PartyWithMeta[]) => {
        let women = 0;
        let men = 0;

        group.forEach(({ partyData }) => {
            partyData.clenove.forEach((member) => {
                if (member.aktivni === 1) {
                    if (member.pohlavi === 1) {
                        women += 1;
                    } else {
                        men += 1;
                    }
                }
            });
        });

        if (women + men === 0) return null;

        const womenPercent = (women / (women + men)) * 100;
        const averageAttendance = weightedAttendance(group);

        return t('Groups.summary', {
            women: t('Groups.womenCount', { count: women }),
            men: t('Groups.menCount', { count: men }),
            womenPercent: formatter.percentFrom100(womenPercent),
            attendance: averageAttendance !== null ? formatter.percentFrom100(averageAttendance) : '—'
        });
    };

    const renderGroup = (label: string, group: PartyWithMeta[]) => {
        if (group.length === 0) return null;

        const totalMandates = group.reduce((sum, { partyData }) => sum + partyData.pocet_aktivnich, 0);
        const summary = buildGroupSummary(group);

        return (
            <ContentSection
                key={label}
                title={`${label} · ${totalMandates} ${t('mandate', { ns: 'translation', count: totalMandates })}`}
                subtitle={summary ?? undefined}
                className='parties-group'
                contentClassName='card-list'
            >
                {group.map(cardComponent)}
            </ContentSection>
        );
    };

    if (partiesData !== undefined) {
        const partiesWithMeta: PartyWithMeta[] = partiesData.map((partyData) => {
            const stats = partiesStats.find(
                (p: { strana_zkratka: string }) => p.strana_zkratka === partyData.strana_zkratka
            ) as PartyStats;
            const isInCoalition = !!basicData.koalice.find(
                (koaliceParty: { strana_zkratka: string }) => koaliceParty.strana_zkratka === partyData.strana_zkratka
            );
            const isInactive = partyData.pocet_aktivnich === 0;

            return { partyData, stats, isInCoalition, isInactive };
        });

        const byMandateCount = (a: PartyWithMeta, b: PartyWithMeta) =>
            b.partyData.pocet_aktivnich - a.partyData.pocet_aktivnich;

        const coalitionGroup = partiesWithMeta.filter((p) => p.isInCoalition).sort(byMandateCount);
        const oppositionGroup = partiesWithMeta.filter((p) => !p.isInCoalition).sort(byMandateCount);
        // Without a coalition configured for this term (e.g. the European Parliament),
        // every party ends up in "oppositionGroup" by default — labelling all of them
        // "Opposition" would misleadingly imply a coalition/opposition split that doesn't exist.
        const remainingGroupLabel = coalitionGroup.length > 0 ? t('opposition') : t('parties');

        const strongestParty = [...partiesData].sort((a, b) => b.pocet_aktivnich - a.pocet_aktivnich)[0];
        const totalMandates = partiesWithMeta.reduce((sum, { partyData }) => sum + partyData.pocet_aktivnich, 0);
        const overallAttendance = weightedAttendance(partiesWithMeta);

        return (
            <div className='page-dashboard parties-page'>
                <div className='page-header-row'>
                    <ContentSection
                        title={t('parties')}
                        level={1}
                        subtitle={basicData.funkcni_obdobi}
                        className='page-title-section'
                    />
                    <div className='stat-chip-row'>
                        <StatChip icon='diversity_3' value={partiesData.length} label={t('parties')} />
                        <StatChip
                            icon='chair'
                            value={totalMandates}
                            label={t('mandate', { ns: 'translation', count: totalMandates })}
                        />
                        <StatChip
                            icon='group'
                            value={overallAttendance !== null ? formatter.percentFrom100(overallAttendance) : '—'}
                            label={t('attendance', { ns: 'meetings' })}
                        />
                        <StatChip icon='military_tech' value={strongestParty.strana_zkratka} label={t('strongestSubject')} />
                    </div>
                </div>
                <div className='page-description'>{t('partiesOverview')}</div>

                <div className='parties-search-block'>
                    <InputText
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t('Search.placeholder')}
                        aria-label={t('Search.placeholder')}
                        className='parties-search-input'
                    />
                </div>

                {trimmedQuery ? (
                    <ContentSection
                        title={
                            searchResults.length > 0
                                ? t('totalResults', {
                                      ns: 'translation',
                                      totalCount: searchResults.length,
                                      count: searchResults.length
                                  })
                                : t('Search.noResults')
                        }
                        className='dashboard-card'
                    >
                        {searchResults.length > 0 && (
                            <div className='member-card-list'>
                                {searchResults.map(({ member, partyZkratka, partyBarva }) => {
                                    const nameParts = member.jmeno.split(' ');
                                    const firstName = nameParts[0];
                                    const lastName = nameParts.slice(1).join(' ');

                                    return (
                                        <MemberCard
                                            key={member.id}
                                            memberData={{
                                                id: member.id,
                                                firstName,
                                                lastName,
                                                gender: (member.pohlavi ?? 0) as 0 | 1,
                                                photo: member.photo,
                                                inactive: !member.aktivni
                                            }}
                                            partyData={{ partyName: partyZkratka, partyColor: partyBarva }}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </ContentSection>
                ) : (
                    <>
                        {renderGroup(t('coalition'), coalitionGroup)}
                        {coalitionGroup.length > 0 && oppositionGroup.length > 0 && (
                            <Divider layout='horizontal' className='parties-group-divider' />
                        )}
                        {renderGroup(remainingGroupLabel, oppositionGroup)}
                    </>
                )}
            </div>
        );
    }

    return null;
}

export default Parties;
